import { useEffect, useState } from "react";
import { duviNicheSiteBuildRegisterUrl } from "@/config";
import {
  parseDuviNicheSiteTiersDocument,
  type DuviNicheSiteTierDocumentRow,
} from "./duvi-niche-site-tiers-document";
import {
  duviNicheSiteTierFeatures,
  DUVI_NICHE_SITE_TIER_PRICE,
  DUVI_NICHE_SITE_TIER_PRICE_PERIOD,
} from "./duvi-niche-site-tier-copy";
import type {
  DuviNicheSitePricingFaq,
  DuviNicheSitePricingModel,
  DuviNicheSitePricingTier,
  DuviNicheSitePricingTiersState,
} from "./duvi-niche-site-pricing.types";

/**
 * FAQ copy, from the bespoke Fishwalkers brochure prototype's "Questions
 * walkers ask" list (duvi-design/exports/niches/fishwalkers/brochure.zip).
 * At most two sentences per answer, in the design system's committed voice.
 */
const FAQS: DuviNicheSitePricingFaq[] = [
  {
    question: "Do I need to pay to sign up?",
    answer: "No — pick a plan and start setting up your page today.",
  },
  {
    question: "Can I change plans later?",
    answer: "Yes, anytime from your dashboard.",
  },
  {
    question: "What happens after I sign up?",
    answer:
      "You'll add your walk types, set your round and upload a few photos before going live.",
  },
];

/**
 * Ordering: price_pence ascending, tied on `name` ascending, tied on
 * `created_at` ascending. Byte-for-byte the same rule as duvi-niche-api's
 * `sortTiersByPrice` (the one ordering authority for every tier listing
 * this platform emits) and duvi-niche-business-www's publish worker, which
 * independently reproduces it server-side — this is the THIRD
 * implementation of that rule (no shared artefact can span a Go Lambda and
 * a static site), and it MUST NOT drift from the other two. `created_at` is
 * plain string comparison: the wire value is RFC3339 fixed-width UTC
 * seconds, whose lexicographic order matches chronological order. Sorted
 * client-side regardless of what order the document itself arrives in, per
 * story 155's brief ("do not rely on document order").
 */
function compareTierRows(
  a: DuviNicheSiteTierDocumentRow,
  b: DuviNicheSiteTierDocumentRow,
): number {
  if (a.price_pence !== b.price_pence) return a.price_pence - b.price_pence;
  if (a.name !== b.name) return a.name < b.name ? -1 : 1;
  if (a.created_at !== b.created_at) {
    return a.created_at < b.created_at ? -1 : 1;
  }
  return 0;
}

function toPricingTiers(
  rows: DuviNicheSiteTierDocumentRow[],
): DuviNicheSitePricingTier[] {
  const sorted = [...rows].sort(compareTierRows);
  return sorted.map((row) => ({
    id: row.tier_id,
    displayName: row.display_name,
    description: row.description,
    price: DUVI_NICHE_SITE_TIER_PRICE,
    pricePeriod: DUVI_NICHE_SITE_TIER_PRICE_PERIOD,
    features: duviNicheSiteTierFeatures(row.name),
    registerUrl: duviNicheSiteBuildRegisterUrl(row.name),
    // Read, never derived: `is_highlighted` is the producer's own answer
    // (design/contracts.md "The highlighted tier and the default tier").
    // A document with no `true` row yields no highlighted card — that is a
    // real, valid state, not a prompt to elect one positionally.
    highlighted: row.is_highlighted,
  }));
}

/**
 * useDuviNicheSitePricing — fetches this site's own `/tiers.json`
 * (same-origin, no auth, cacheable — a static document at the site root,
 * not an API call; constitution VIII, this repo's CLAUDE.md) and derives
 * the pricing card model from it, replacing the old hard-coded tier-name
 * source literal that let an admin rename never reach this page
 * (story 155). Loading and "unavailable" (404, network failure, or a
 * document that fails shape validation) are both exposed via `tiersState`
 * so the view can render a non-crashing fallback — see
 * `duvi-niche-site-pricing-view.tsx`.
 */
export function useDuviNicheSitePricing(): DuviNicheSitePricingModel {
  const [tiersState, setTiersState] = useState<DuviNicheSitePricingTiersState>(
    { status: "loading" },
  );

  useEffect(() => {
    let cancelled = false;

    fetch("/tiers.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`/tiers.json responded HTTP ${response.status}`);
        }
        return response.json() as Promise<unknown>;
      })
      .then((body) => {
        if (cancelled) return;
        const rows = parseDuviNicheSiteTiersDocument(body);
        if (!rows || rows.length === 0) {
          setTiersState({ status: "unavailable" });
          return;
        }
        setTiersState({ status: "ready", tiers: toPricingTiers(rows) });
      })
      .catch(() => {
        if (cancelled) return;
        setTiersState({ status: "unavailable" });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    registerUrl: duviNicheSiteBuildRegisterUrl(),
    tiersState,
    faqs: FAQS,
  };
}
